import { QuestionnaireComponentKind } from '@main/dynamic-form';
import { useCallback, useMemo } from 'react';

import { getQuestionConditionsFilter } from '../utils/conditions';
import { getFormConfigFilter } from '../utils/fields';
import { QuestionnaireFormContextValue, useQuestionnnaireFormContext } from './context';
import { QuestionnaireFormFieldConfig } from './form-types';

export function useVisibleFields(
  fields?: readonly QuestionnaireFormFieldConfig[],
  context?: QuestionnaireFormContextValue,
) {
  const { getAllVisibleFields } = useVisibleFieldsEx(context);
  return useMemo(() => getAllVisibleFields(fields), [fields, getAllVisibleFields]);
}

export function useVisibleFieldsEx(context?: QuestionnaireFormContextValue) {
  const { answers, answersMap, flatFields, filtersFilter, shouldSkipFilteringFields } =
    useQuestionnnaireFormContext(context);

  const getAllVisibleFields = useCallback(
    (fields?: readonly QuestionnaireFormFieldConfig[]) => {
      const conditionsFilterMap = new Map(
        fields?.map((field) => [field.name, getQuestionConditionsFilter(field, flatFields, true)]),
      );

      const filteredFields =
        (shouldSkipFilteringFields
          ? fields
          : fields?.filter(getFormConfigFilter(answers, answersMap, conditionsFilterMap))) ?? [];

      const visibleFields = filteredFields.filter(
        (field) => !filtersFilter(field, answersMap.get(field.name)),
      );

      return { visibleFields, filteredFields };
    },
    [answers, answersMap, flatFields, filtersFilter, shouldSkipFilteringFields],
  );

  const getVisibleFieldsRecursive = useCallback(
    (fields?: readonly QuestionnaireFormFieldConfig[]): QuestionnaireFormFieldConfig[] => {
      const { visibleFields } = getAllVisibleFields(fields);

      return visibleFields.map((field) => {
        if (field.kind === QuestionnaireComponentKind.Section && field.fields) {
          const visibleFields = getVisibleFieldsRecursive(field.fields);
          return { ...field, fields: visibleFields };
        }

        return field;
      });
    },
    [getAllVisibleFields],
  );

  const getFilteredFieldsRecursive = useCallback(
    (fields?: readonly QuestionnaireFormFieldConfig[]): QuestionnaireFormFieldConfig[] => {
      const { filteredFields } = getAllVisibleFields(fields);

      return filteredFields.map((field) => {
        if (field.kind === QuestionnaireComponentKind.Section && field.fields) {
          const visibleFields = getFilteredFieldsRecursive(field.fields);
          return { ...field, fields: visibleFields };
        }

        return field;
      });
    },
    [getAllVisibleFields],
  );

  return { getAllVisibleFields, getVisibleFieldsRecursive, getFilteredFieldsRecursive };
}
